import {type DependencyList, useEffect, useRef, useState} from 'react';

import {useIsMounted} from './use-is-mounted';
import {shallowCompare} from './shallow-diff';

/**
 * A convenience hook to handle promises in React components, where the result
 * of the promise is needed.
 *
 * I've taken inspiration from the `useImport` hook to use the `useIsMounted` hook.
 *
 * @param makePromise `() => Promise<T>`
 * @param dependencies `DependencyList`
 * @param options.withTrailing `boolean`: keeps the previous resolved value when resolving new promise
 * @returns `{status: 'pending'} | {status: 'resolved'; result: T}`
 */
export const usePromise = <T>(
    makePromise: () => Promise<T>,
    dependencies: DependencyList,
    options: {withTrailing?: boolean} = {},
) => {
    const {withTrailing} = options;

    // Recreate the promise only when `dependencies` change, mirroring the `useEffect` deps
    // contract. We memoise manually via a ref (rather than `useMemo` with a caller-provided deps
    // array) because a dynamic dependency list cannot be statically verified by the hooks linter.
    const cacheRef = useRef<{dependencies: DependencyList; promise: Promise<T>} | null>(null);
    if (cacheRef.current === null || shallowCompare(cacheRef.current.dependencies, dependencies)) {
        cacheRef.current = {dependencies, promise: makePromise()};
    }
    const stablePromise = cacheRef.current.promise;

    const [state, setState] = useState<PromiseResolution<T, InstanceType<typeof Error>>>(makePending);

    const isMounted = useIsMounted();

    useEffect(() => {
        stablePromise
            .then(result => {
                if (isMounted()) {
                    setState(wrapResolved(result));
                }
            })
            .catch(exception => {
                if (isMounted()) {
                    setState(wrapRejected(exception));
                }
            });
        return () => {
            if (!withTrailing) {
                setState(makePending());
            }
        };
    }, [isMounted, stablePromise, withTrailing]);

    return state;
};

export type PromiseResolution<T = void, E = void> =
    | {status: 'pending'}
    | {status: 'rejected'; exception: E}
    | {status: 'resolved'; result: T};

const wrapResolved = <T>(result: T): Extract<PromiseResolution<T>, {status: 'resolved'}> => ({
    status: 'resolved' as const,
    result,
});
const wrapRejected = <E>(exception: E): Extract<PromiseResolution<void, E>, {status: 'rejected'}> => ({
    status: 'rejected' as const,
    exception,
});
export const makePending = (): Extract<PromiseResolution, {status: 'pending'}> => ({status: 'pending' as const});

export const wrapPromise = <T>(promise: Promise<T>) => {
    return promise
        .then(result => {
            return wrapResolved(result);
        })
        .catch(exception => {
            return wrapRejected(exception);
        });
};
